/* eslint-disable camelcase */

export interface Request {
  query: {
    /** *************************************************** READ ME *****************************************************
      Below, you can find the list of query parameters that can be passed to the interstitial-client, including the
      required parameters for Segment shopping finished events. Unless otherwise indicated, all query parameters are 
      optional. Required parameters are marked with the `REQUIRED` tag.

      Segment: 
        Interstitial client maps certain query parameters to Segment shopping finish fields used for revenue tracking. 
        Therefore, it is crucial the required Shopping Finished fields are passed properly to interstitial-client, so
        the correct Shopping Finished event data is sent to Segment by interstitial client. Please see 
        https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/2628029364/Standard+Shopping+Events#3.-Shopping-Event-Attributes-%5B2023.05.04%5D
        for the most up to date required and optional parameters for Segment Shopping Finished events.
    ***************************************************** READ ME **************************************************** */

    /** not to be used in production; test parameters to suppress redirects */
    suppress?: string; // Existence of the parameter suppresses the redirect, and stays on the interstitial-client page.
    // You may use this to check the full destination URL without the page redirecting.

    /** customizable destination URL */
    override?: string; // Overrides the default destination URL for the offer. The override url needs be URI-encoded.
    // By default, some internal NerdWallet urls are allowlisted for all redirects with prefix in the list
    // OVERRIDE_ALLOWED_PREFIX_PATHS. See
    // https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/692749094/Marketplace+Redirects#Overrides for more information.
    // If you'd like to override the default destination URL for a specific offer that is external to the NerdWallet domain,
    // we recommend you use alternative links. See the `alt` parameter for more details.
    alt?: string; // Overrides the default destination URL for the offer; should match the `name` field of the alternative
    // link you'd like to use in the "Alternative Links" section in internal details of an offer. See
    // https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/edit-v2/692749094#Alternate-Links
    destination_url_field?: string; // Valid values: 'terms_and_fees_link'; used to override the default destination URL
    // for the offer with the terms and fees link for credit cards; this is passed via the apply_link generated by
    // marketplace. This parameter is DEPRECATED, please use the above options instead of this one.

    /** custom server-side redirect behavior */
    serverRedirect?: string; // 'true' to perform a server redirect instead of a client redirect. The redirect destination url
    // origin must be allowlisted in the env var SERVER_REDIRECT_ALLOWED_DESTINATION_ORIGINS. The server redirect performed
    // returns 307 Temporary Redirect status code. The Shopping Finished event will still be sent to Segment before the
    // redirect occurs. When server redirect is performed, any Javascript and HTML code, including GTM and the interstitial UI,
    // on the interstitial-client page will not be executed. Currently, server-side redirects are not supported for investing
    // pages that need to display a disclaimer to the user before redirecting to the partner site.

    /** custom UI behavior */
    supressSplashPage?: string; // 'true' to show a blank interstitial-client page
    interstitial_art?: string; // Used to display credible card art on the interstitial-client page

    /** custom tracking behavior */
    suppress_tracking?: string; // '1' to suppress tracking; Will not send Shopping finished events to Segment,

    /** Turnstile test hooks */
    ts_fail?: string; // '1' forces the Cloudflare Turnstile verification to fail by
    // swapping the live site/secret keys for Cloudflare's published always-fails test keys
    // at both render and verify time. Values other than "1" are ignored. Only takes
    // effect on routes already gated by Turnstile (see TURNSTILE_ENABLED_PRODUCT_TYPES);
    // ignored silently otherwise.

    /** custom query params passed to the destination URL */
    partnerParams?: string; // See https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/edit-v2/692749094#Partner-Params
    sharedid?: string; // Currently, only used for overriding the sharedid of Discover mobile users used by credit cards.
    // This parameter is DEPRECATED, please use the above options instead of this one.

    /** used for Segment events */
    ajs_aid?: string; // anonymous id. This takes precedence over ajs_anonymous_id cookie. See
    // https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/2980970650/FAQ+There+is+no+anonymous+ID+from+Interstitial+Client
    ajs_uid?: string; // user id. This takes precedence over ajs_user_id cookie. See
    // https://nerdwallet.atlassian.net/wiki/spaces/PEDD/pages/2980970650/FAQ+There+is+no+anonymous+ID+from+Interstitial+Client
    calling_deployable_name?: string; // REQUIRED. The name of the deployable that is calling the interstitial-client; AKA
    // front-end client, or front-end deployable
    calling_deployable_version?: string; // REQUIRED
    cta_type?: string; // REQUIRED. See CtaType for a list of valid values
    element_position?: string;
    entry_point_page_name?: string;
    entry_point_page_url?: string;
    entry_point_pageview_id?: string;
    finish_type?: string; // REQUIRED. See FinishType for a list of valid values.
    impression_pageview_id?: string; // REQUIRED
    impression_page_name?: string;
    impression_page_url?: string; // By default, interstitial client uses the header referer, but front-end clients
    // can override this by passing this parameter. Refer to
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Referrer-Policy for more information on how the referer
    // header gets populated by the browser. Most modern browsers use `strict-origin-when-cross-origin` as the default.
    impression_position?: string; // REQUIRED
    impression_section?: string; // REQUIRED
    monetizable?: string; // See MonetizableType for a list of valid values. By default, interstitial client uses the
    // monetizable information set in the marketplace data, but front-end clients can override this by passing this
    // parameter
    section_position?: string;
    shopping_finish_id?: string; // This should only be passed if the front-end client is firing it's own Segment Shopping Finished
    // event. Interstitial client will also fire a tracking event that will be used as a fallback with this shopping_finish_id.
    sku_impression_id?: string; // REQUIRED
    sub_id_alias?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_term?: string;

    /** DEPRECATED params. These are duplicates of the above Segment parameters and are still used by front-end clients
     * and supported in interstitial-client for backwards compatibility. These should be removed in the future.
     */
    clickHeader_impressionId?: string; // legacy field for sku_impression_id
    clickHeader_productLocation?: string; // legacy field for impression_section
    clickHeader_linkType?: string; // legacy field for cta_type
    finish_id?: string; // legacy field for shopping_finish_id
    impression_id?: string; // legacy field for sku_impression_id
    link_type?: string; // legacy field for cta_type
    product_impression_id?: string; // legacy field for sku_impression_id
    section_name?: string; // legacy field for impression_section
    source_url?: string; // legacy field for impression_page_url
    url?: string; // legacy field for impression_page_url

    /** allows for the passing of arbitrary query parameters */
    [key: string]: string | undefined;
  };
  headers: {
    // used for GTM price
    'user-agent'?: string;

    // used for Segment
    referer?: string;
    'x-cookie-id'?: string;
    'x-original-url'?: string;
    'x-request-url'?: string;

    // used internally by interstitial client for displaying error page
    'impression-page-url'?: string;

    // miscellaneous
    'x-in-native-app'?: string; // indicates if the request is coming from a mobile app

    // used for logging to Coralogix only
    'x-browser-session-id'?: string;
    'x-caller-client-id'?: string;
    'x-forwarded-for'?: string;
    'x-method'?: string;
    'x-originating-ip'?: string;
    'x-originating-user-agent'?: string;
    'x-originating-url'?: string;
    'x-pageview-id'?: string;
    'x-request-id'?: string; // used for logging to Datadog as well
    'x-cf-bot-score'?: string; // Cloudflare bot score
    'x-cf-verified-bot'?: string; // Cloudflare verified bot score
    'cf-ray'?: string; // Cloudflare ray id
  };
  cookies: {
    // used for Segment
    ajs_anonymous_id?: string;
    ajs_user_id?: string;

    // used to validate marketplace token
    cookie_id?: string;
  };
}

export function getHeaders(headers: any): Request['headers'] {
  const result: Request['headers'] = {};
  headers.forEach((value: string, key: string) => {
    result[key as keyof Request['headers']] = value;
  });
  return result;
}

export function getCookies(cookies: any): Request['cookies'] {
  return cookies
    .getAll()
    .reduce(
      (map: { [key: string]: string }, cookie: { name: any; value: any }) => {
        return { ...map, [cookie.name]: cookie.value };
      },
      {}
    );
}

export function getRequest(
  searchParams: Record<string, any>,
  headers: any,
  cookies: any
): Request {
  const h = getHeaders(headers);

  return {
    // For each key-value pair in `searchParams`, if the value is an array, only its first
    // element is retained in the resulting object; otherwise, the original value is preserved.
    query: Object.fromEntries(
      Object.entries(searchParams).map(([key, value]) =>
        Array.isArray(value) ? [key, value[0]] : [key, value]
      )
    ) as Request['query'],
    headers: h,
    cookies: getCookies(cookies),
  };
}

export const ALL_QUERY_PARAMS = [
  'suppress',
  'override',
  'alt',
  'destination_url_field',
  'serverRedirect',
  'supressSplashPage',
  'interstitial_art',
  'suppress_tracking',
  'ts_fail',
  'partnerParams',
  'sharedid',
  'ajs_aid',
  'ajs_uid',
  'calling_deployable_name',
  'calling_deployable_version',
  'cta_type',
  'element_position',
  'entry_point_page_name',
  'entry_point_page_url',
  'entry_point_pageview_id',
  'finish_type',
  'impression_pageview_id',
  'impression_page_name',
  'impression_page_url',
  'impression_position',
  'impression_section',
  'monetizable',
  'section_position',
  'shopping_finish_id',
  'sku_impression_id',
  'sub_id_alias',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
  'clickHeader_impressionId',
  'clickHeader_productLocation',
  'clickHeader_linkType',
  'finish_id',
  'impression_id',
  'link_type',
  'product_impression_id',
  'section_name',
  'source_url',
  'url',
];

/* This is a mapping of legacy query params to current query params. Legacy params that contain
 * a replacement parameter are DEPRECATED, but still currently supported in interstitial-client for backwards
 * compatibility. Note that the replacement parameter option(s) are drop in replacements for the legacy
 * parameter.
 */
export const LEGACY_PARAMS_TO_CURRENT_PARAMS: Record<string, string[]> = {
  clickHeader_impressionId: ['sku_impression_id'],
  clickHeader_productLocation: ['impression_section'],
  clickHeader_linkType: ['cta_type'],
  finish_id: ['shopping_finish_id'],
  impression_id: ['sku_impression_id'],
  link_type: ['cta_type'],
  product_impression_id: ['sku_impression_id'],
  section_name: ['impression_section'],
  source_url: ['impression_page_url'],
  url: ['impression_page_url'],
};

/* This is a mapping of special legacy query params to current query params that may be a little more
 * complex than a simple drop-in replacement. These legacy params are DEPRECATED, but still currently
 * supported in interstitial-client for backwards compatibility.
 */
export const SPECIAL_LEGACY_PARAMS_TO_CURRENT_PARAMS: Record<string, string[]> =
  {
    destination_url_field: ['override', 'alt'], // can be achieved by using either of the two instead of destination_url_field
    sharedid: ['partnerParams'], // can be achieved by using partnerParams instead of sharedid
  };
